<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CoLivingTabs from './CoLivingTabs.vue';
import RoleModal from './components/RoleModal.vue';
import {
  apiErrorCode,
  apiErrorDetail,
  apiErrorMessage,
} from '../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from './constants';
import { ROLE_KINDS, usedByKeys } from './catalogForms';

export default {
  name: 'CoLivingRolesPage',
  components: {
    PageHeader,
    LoadingState,
    DeleteModal,
    CoLivingTabs,
    RoleModal,
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      kinds: ROLE_KINDS,
      search: '',
      kindFilter: '',
      statusFilter: 'all',
      loadError: '',
      isModalOpen: false,
      editingRole: null,
      roleToDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingCatalog', {
      roles: 'getRoles',
      uiFlags: 'getUiFlags',
    }),
    visibleRoles() {
      const q = this.search.trim().toLowerCase();
      return this.roles.filter(role => {
        if (this.kindFilter && role.kind !== this.kindFilter) return false;
        if (this.statusFilter === 'active' && !role.active) return false;
        if (this.statusFilter === 'archived' && role.active) return false;
        return (
          !q ||
          role.key.toLowerCase().includes(q) ||
          role.label.toLowerCase().includes(q)
        );
      });
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions('coLivingCatalog', ['fetchRoles', 'removeRole']),
    async load() {
      this.loadError = '';
      try {
        await this.fetchRoles();
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    openAdd() {
      this.editingRole = null;
      this.isModalOpen = true;
    },
    openEdit(role) {
      this.editingRole = role;
      this.isModalOpen = true;
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await this.removeRole(this.roleToDelete.key);
        useAlert(this.$t('CO_LIVING.ROLES.DELETED'));
        this.roleToDelete = null;
      } catch (err) {
        if (apiErrorCode(err) === 'in_use') {
          const used = usedByKeys(apiErrorDetail(err))
            .map(key => this.$t(`CO_LIVING.IN_USE.${key}`))
            .join(', ');
          useAlert(this.$t('CO_LIVING.ROLES.IN_USE', { used }));
        } else {
          useAlert(apiErrorMessage(this, err, 'CO_LIVING.ROLES.DELETE_FAILED'));
        }
        this.roleToDelete = null;
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-roles-page">
    <PageHeader
      :title="$t('CO_LIVING.TITLE')"
      icon="condo"
      :description="$t('CO_LIVING.DESCRIPTION')"
    />
    <CoLivingTabs />

    <div class="flex items-start justify-between gap-4 mb-4">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.ROLES.INTRO') }}
      </p>
      <woot-button icon="add" data-testid="co-living-add-role" @click="openAdd">
        {{ $t('CO_LIVING.ROLES.ADD_ROLE') }}
      </woot-button>
    </div>

    <div class="flex flex-wrap gap-3 mb-4">
      <input
        v-model="search"
        type="search"
        :placeholder="$t('CO_LIVING.ROLES.SEARCH')"
        class="max-w-xs"
        :class="[fieldClass]"
        data-testid="co-living-role-search"
      />
      <select
        v-model="kindFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-role-kind-filter"
      >
        <option value="">{{ $t('CO_LIVING.ROLES.ALL_KINDS') }}</option>
        <option v-for="kind in kinds" :key="kind" :value="kind">
          {{ $t(`CO_LIVING.ROLES.KINDS.${kind.toUpperCase()}`) }}
        </option>
      </select>
      <select
        v-model="statusFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-role-status-filter"
      >
        <option value="all">{{ $t('CO_LIVING.COMMON.ALL_STATUSES') }}</option>
        <option value="active">{{ $t('CO_LIVING.COMMON.ACTIVE') }}</option>
        <option value="archived">{{ $t('CO_LIVING.COMMON.ARCHIVED') }}</option>
      </select>
    </div>

    <LoadingState
      v-if="uiFlags.isFetchingRoles && !roles.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>
    <p
      v-else-if="!roles.length"
      class="text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-roles-empty"
    >
      {{ $t('CO_LIVING.ROLES.EMPTY') }}
    </p>
    <p
      v-else-if="!visibleRoles.length"
      class="text-sm text-slate-500 dark:text-slate-400"
    >
      {{ $t('CO_LIVING.COMMON.NO_MATCHES') }}
    </p>
    <div
      v-else
      class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
    >
      <table class="w-full text-sm" data-testid="co-living-roles-table">
        <thead>
          <tr class="text-left text-slate-500 dark:text-slate-400">
            <th class="p-3">{{ $t('CO_LIVING.ROLES.COLUMNS.ROLE') }}</th>
            <th class="p-3">{{ $t('CO_LIVING.ROLES.COLUMNS.KIND') }}</th>
            <th class="p-3">{{ $t('CO_LIVING.ROLES.COLUMNS.MEMBERS') }}</th>
            <th class="p-3">{{ $t('CO_LIVING.COMMON.STATUS') }}</th>
            <th class="p-3" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="role in visibleRoles"
            :key="role.key"
            class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
            :data-testid="`co-living-role-row-${role.key}`"
          >
            <td class="p-3">
              {{ role.label }}
              <span class="block font-mono text-xs text-slate-500">
                {{ role.key }}
              </span>
            </td>
            <td class="p-3">
              {{ $t(`CO_LIVING.ROLES.KINDS.${role.kind.toUpperCase()}`) }}
            </td>
            <td class="p-3">
              <span
                v-if="!role.member_count"
                class="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300"
              >
                {{ $t('CO_LIVING.ROLES.NO_ONE') }}
              </span>
              <template v-else>{{ role.member_count }}</template>
            </td>
            <td class="p-3">
              {{
                role.active
                  ? $t('CO_LIVING.COMMON.ACTIVE')
                  : $t('CO_LIVING.COMMON.ARCHIVED')
              }}
            </td>
            <td class="p-3 text-right whitespace-nowrap">
              <woot-button
                v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                variant="clear"
                size="small"
                icon="edit"
                :data-testid="`co-living-role-edit-${role.key}`"
                :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                @click="openEdit(role)"
              />
              <woot-button
                v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                variant="clear"
                size="small"
                color-scheme="alert"
                icon="delete"
                :data-testid="`co-living-role-delete-${role.key}`"
                :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                @click="roleToDelete = role"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <RoleModal
      v-if="isModalOpen"
      :role="editingRole"
      @close="isModalOpen = false"
    />
    <DeleteModal
      v-if="roleToDelete"
      :delete-name="roleToDelete.label"
      :is-loading="isDeleting"
      @close="roleToDelete = null"
      @confirm="confirmDelete"
    >
      <p class="mt-2 text-sm text-slate-500">
        {{ $t('CO_LIVING.ROLES.DELETE_NOTE') }}
      </p>
    </DeleteModal>
  </div>
</template>
